import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';

// React Native Reusables Components
import { Card, CardContent } from '@/components/ui/card';
import { Text } from '@/components/ui/text';

// Services & Types
import { apiRequest } from '../../services/apiClient';
import { AdminTabBar, ADMIN_COLORS, type AdminTab } from '../../components/admin/AdminUI';
import { isSchoolAdmin, isSuperAdmin, canManageMentors, User } from '../../utils/roleUtils';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import InvitePersonModal, { type InviteRole } from '../../components/school/InvitePersonModal';
import StudentDetailModal from '../../components/school/StudentDetailModal';
import { MentorListSection } from '../../components/school/MentorListSection';
// MentorInviteModal removed — folded into the unified InvitePersonModal
// (Mentör tab). Kept the file in case other surfaces still reference it.
import FrameworkEditorModal from '../../components/school/FrameworkEditorModal';
import DocumentFrameworkLinkSheet from '../../components/school/DocumentFrameworkLinkSheet';
import { restrictedTabFallback } from './schoolTabs';
import CohortsTab from '../../components/school/CohortsTab';
import EngagementRosterTab from '../../components/school/EngagementRosterTab';
import {
  COLORS,
  type DashboardAnalytics,
  type PendingInvitation,
  type School,
  type Student,
} from './schoolManagementTypes';
import { OverviewTab } from './tabs/OverviewTab';
import { SettingsTab } from './tabs/SettingsTab';
import { StudentsTab } from './tabs/StudentsTab';
import { useSchoolStudents } from './useSchoolStudents';
import { DocumentsTab } from './tabs/DocumentsTab';
import { FrameworksTab } from './tabs/FrameworksTab';
import { useSchoolDocuments } from './useSchoolDocuments';
import { useSchoolFrameworks } from './useSchoolFrameworks';

interface SchoolManagementScreenProps {
  user?: User;
  onLogout?: () => void;
}

/**
 * Enhanced School Management Screen - Professional Design
 *
 * - Professional design tokens (COLORS) for consistent styling
 * - Complete i18n support with no hardcoded text
 * - Proper safe area handling for iOS native tab bar
 * - React Native Reusables components for consistent UI
 * - Responsive layout with professional appearance
 */
export default function SchoolManagementScreenEnhanced({ user }: SchoolManagementScreenProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const params = useLocalSearchParams<{ tab?: string | string[]; schoolId?: string | string[] }>();

  const getFirst = (value?: string | string[]) =>
    Array.isArray(value) ? value[0] : value;

  const tabParam = getFirst(params.tab);
  const normalizedTab = tabParam === 'students' || tabParam === 'cohorts' || tabParam === 'engagements' || tabParam === 'mentors' || tabParam === 'documents' || tabParam === 'frameworks' || tabParam === 'settings'
    ? tabParam
    : 'overview';
  const schoolIdParam = getFirst(params.schoolId);
  const selectedSchoolId = schoolIdParam ? Number(schoolIdParam) : undefined;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [school, setSchool] = useState<School | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvitation[]>([]);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'students' | 'cohorts' | 'engagements' | 'mentors' | 'documents' | 'frameworks' | 'settings'>(normalizedTab);
  const [invitePerson, setInvitePerson] = useState<{ visible: boolean; role: InviteRole }>({
    visible: false,
    role: 'student',
  });
  const openInvite = (role: InviteRole) => setInvitePerson({ visible: true, role });
  const closeInvite = () => setInvitePerson((s) => ({ ...s, visible: false }));
  // Legacy alias kept while we migrate per-trigger usages below.
  const setShowInviteModal = (v: boolean) => (v ? openInvite('student') : closeInvite());
  // Legacy alias for the mentor-specific invite trigger (now routes through
  // the unified InvitePersonModal with role='mentor').
  const setShowMentorInviteModal = (v: boolean) => (v ? openInvite('mentor') : closeInvite());
  const [mentorRefreshKey, setMentorRefreshKey] = useState(0);

  const canAccessPrivateSchoolKnowledge = Boolean(
    user &&
    school &&
    (
      isSuperAdmin(user) ||
      (
        isSchoolAdmin(user) &&
        typeof user.school_id === 'number' &&
        user.school_id === school.id
      )
    )
  );

  // The URL→state sync effect lives on its own farther down (depends only
  // on `normalizedTab`). Don't add `activeTab` to its deps — doing so was
  // the original tab-switching bug: every click changed `activeTab`, the
  // effect re-fired, and reset to `normalizedTab` (URL hadn't moved), so
  // the click was instantly undone. See PlatformAdminScreenEnhanced for
  // the same fix pattern.

  useEffect(() => {
    setActiveTab(normalizedTab);
  }, [normalizedTab]);

  const loadSchoolData = React.useCallback(async () => {
    try {
      setLoading(true);

      const fetchSchool = async (id: number) => {
        return await apiRequest({ endpoint: `/schools/${id}`, method: 'GET' });
      };

      const fetchStudents = async (id: number) => {
        return await apiRequest({ endpoint: `/schools/${id}/students`, method: 'GET' });
      };

      const fetchPendingInvites = async (id: number) => {
        try {
          return await apiRequest<PendingInvitation[]>({ endpoint: `/schools/${id}/student-invitations?status=pending`, method: 'GET' });
        } catch (error) {
          console.error('Error fetching invitations:', error);
          return [];
        }
      };

      const fetchAnalytics = async (id: number) => {
        return await apiRequest({ endpoint: `/school-analytics/${id}/analytics/dashboard`, method: 'GET' });
      };

      if (user && isSuperAdmin(user as User) && selectedSchoolId) {
        const schoolData = await fetchSchool(selectedSchoolId);
        if (schoolData) setSchool(schoolData);

        const [studentsData, invitesData] = await Promise.all([
          fetchStudents(selectedSchoolId),
          fetchPendingInvites(selectedSchoolId)
        ]);

        setStudents(studentsData);
        setPendingInvites(invitesData || []);

        const analyticsData = await fetchAnalytics(selectedSchoolId);
        if (analyticsData) setAnalytics(analyticsData);
      } else if (user?.school_id) {
        const schoolData = await fetchSchool(user.school_id);
        if (schoolData) setSchool(schoolData);

        if (user && isSchoolAdmin(user)) {
          const [studentsData, invitesData] = await Promise.all([
            fetchStudents(user.school_id),
            fetchPendingInvites(user.school_id)
          ]);

          setStudents(studentsData);
          setPendingInvites(invitesData || []);

          const analyticsData = await fetchAnalytics(user.school_id);
          if (analyticsData) setAnalytics(analyticsData);
        }
      }
    } catch (error) {
      console.error('Error loading school data:', error);
      showDialog(t('common.error'), t('schoolManagement.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [selectedSchoolId, user, t]);

  useEffect(() => {
    loadSchoolData();
  }, [loadSchoolData]);

  const onRefresh = async () => {
    setRefreshing(true);
    await loadSchoolData();
    setRefreshing(false);
  };

  const handleInviteSuccess = () => {
    loadSchoolData();
  };

  const openInvitations = () => {
    if (!school?.id) return;
    router.push({ pathname: `/admin/schools/${school.id}/invitations` });
  };

  const roster = useSchoolStudents({
    user,
    school,
    students,
    setStudents,
    pendingInvites,
    activeTab,
    loadSchoolData,
    t,
    showToast,
  });
  const { showStudentDetail, selectedStudentId, handleCloseStudentDetail } = roster;

  // Deleting a framework reloads documents too; loadDocuments comes from
  // the documents hook below (only called after both hooks have run).
  const frameworksState = useSchoolFrameworks({
    school,
    canAccessPrivateSchoolKnowledge,
    setActiveTab,
    reloadDocuments: () => loadDocuments(),
    t,
    showToast,
  });
  const {
    frameworks,
    showFrameworkEditor,
    selectedFrameworkId,
    loadFrameworks,
    loadLinkableFrameworks,
    closeFrameworkEditor,
  } = frameworksState;

  const documentsState = useSchoolDocuments({
    school,
    canAccessPrivateSchoolKnowledge,
    loadLinkableFrameworks,
    t,
    showToast,
  });
  const {
    pendingDocumentUpload,
    loadDocuments,
    cancelPendingDocumentUpload,
    confirmPendingDocumentUpload,
  } = documentsState;

  // Load documents when tab becomes active
  useEffect(() => {
    if (activeTab === 'documents' && school && canAccessPrivateSchoolKnowledge) {
      loadDocuments();
    }
  }, [activeTab, canAccessPrivateSchoolKnowledge, school, loadDocuments]);

  // Load frameworks when tab becomes active
  useEffect(() => {
    if (activeTab === 'frameworks' && school && canAccessPrivateSchoolKnowledge) {
      loadFrameworks();
    }
  }, [activeTab, canAccessPrivateSchoolKnowledge, school, loadFrameworks]);

  useEffect(() => {
    if (activeTab === 'documents' && school && canAccessPrivateSchoolKnowledge && frameworks.length === 0) {
      loadFrameworks();
    }
  }, [activeTab, canAccessPrivateSchoolKnowledge, school, frameworks.length, loadFrameworks]);

  // Only once the school has loaded: before that access is unknown, and
  // resetting then sent `?tab=documents` links to Overview.
  useEffect(() => {
    const fallback = restrictedTabFallback(activeTab, {
      canAccessPrivateSchoolKnowledge,
      schoolLoading: loading,
    });
    if (fallback) setActiveTab(fallback);
  }, [activeTab, canAccessPrivateSchoolKnowledge, loading]);

  // Mentors Section - Dedicated tab for mentor management
  const renderMentors = () => {
    if (!school) return null;
    return (
      <View className="mb-6">
        <MentorListSection
          key={mentorRefreshKey}
          schoolId={school.id}
          maxMentors={5}
          onInvitePress={() => setShowMentorInviteModal(true)}
          onRefresh={() => setMentorRefreshKey((k) => k + 1)}
        />
      </View>
    );
  };

  // Render content based on active tab
  const renderCohorts = () => {
    if (!school) return null;
    return (
      <CohortsTab
        schoolId={school.id}
        students={students.map((s) => ({
          id: s.id,
          email: s.email,
          full_name: s.full_name,
        }))}
        canEdit={isSchoolAdmin(user)}
        onRosterChanged={loadSchoolData}
      />
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <OverviewTab
            analytics={analytics}
            school={school}
            user={user}
            onInviteStudent={() => setShowInviteModal(true)}
            onSelectTab={setActiveTab}
          />
        );
      case 'students':
        return (
          <StudentsTab
            user={user}
            school={school}
            students={students}
            pendingInvites={pendingInvites}
            roster={roster}
            onInviteStudent={() => setShowInviteModal(true)}
            onOpenInvitations={openInvitations}
          />
        );
      case 'cohorts':
        return renderCohorts();
      case 'engagements':
        return <EngagementRosterTab />;
      case 'mentors':
        return renderMentors();
      case 'documents':
        return (
          <DocumentsTab
            user={user}
            canAccessPrivateSchoolKnowledge={canAccessPrivateSchoolKnowledge}
            frameworks={frameworks}
            documentsState={documentsState}
          />
        );
      case 'frameworks':
        return (
          <FrameworksTab
            user={user}
            canAccessPrivateSchoolKnowledge={canAccessPrivateSchoolKnowledge}
            frameworksState={frameworksState}
          />
        );
      case 'settings':
        return <SettingsTab school={school} />;
      default:
        return null;
    }
  };

  // Access control
  if (!user || (!isSchoolAdmin(user) && !isSuperAdmin(user))) {
    return (
      <SafeAreaView className="flex-1 bg-background">
        <Card className="m-4 mt-20">
          <CardContent className="p-8 items-center">
            <MaterialIcons name="error" size={48} color={COLORS.error} />
            <Text className="text-xl font-bold text-red-600 mt-4 mb-2">
              {t('schoolManagement.accessRequired')}
            </Text>
            <Text className="text-base text-muted-foreground text-center">
              {t('schoolManagement.mustBeSchoolAdmin')}
            </Text>
          </CardContent>
        </Card>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1 bg-background">
      {/* Header — no back button: the WebAppShell side-nav is the sole
          top-level navigation. */}
      <View className="bg-white px-6 py-4 border-b border-gray-200">
        <View className="flex-row items-center">
          <Text className="text-2xl font-bold text-foreground">{t('schoolManagement.title')}</Text>
        </View>
      </View>

      <ScrollView
        className="flex-1"
        contentContainerStyle={{
          paddingBottom: Math.max(insets.bottom + 100, 120), // iOS native tab bar safe area
        }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primary} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Max-width container for better UX on large screens */}
        <View className="w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {loading ? (
            <View className="flex-1 items-center justify-center p-10">
              <ActivityIndicator size="large" color={COLORS.primary} />
              <Text className="text-base text-muted-foreground mt-4">
                {t('schoolManagement.loading')}
              </Text>
            </View>
          ) : (
            <>
              {/* Tab Navigation — uses shared AdminTabBar to keep visuals
                  consistent across admin surfaces and to avoid the inline
                  TabButton component pattern (which remounts on every parent
                  render and was eating click events). */}
              <View style={{ marginBottom: 20 }}>
                <AdminTabBar<'overview' | 'students' | 'cohorts' | 'engagements' | 'mentors' | 'documents' | 'frameworks' | 'settings'>
                  tabs={[
                    { key: 'overview', label: t('schoolManagement.overview'), icon: 'dashboard' },
                    { key: 'students', label: t('schoolManagement.students'), icon: 'group' },
                    { key: 'cohorts', label: t('schoolManagement.cohortsTab'), icon: 'groups' },
                    ...(canManageMentors(user)
                      ? [
                          { key: 'mentors' as const, label: t('schoolManagement.mentorsTab'), icon: 'school' as const },
                          { key: 'engagements' as const, label: t('schoolManagement.engagementsTab'), icon: 'assignment-ind' as const },
                        ]
                      : []),
                    ...(canAccessPrivateSchoolKnowledge
                      ? [
                          { key: 'documents' as const, label: t('schoolManagement.documents'), icon: 'folder' as const },
                          { key: 'frameworks' as const, label: t('schoolManagement.frameworks'), icon: 'auto-stories' as const },
                        ]
                      : []),
                    { key: 'settings', label: t('schoolManagement.settings'), icon: 'settings' },
                  ]}
                  activeKey={activeTab}
                  onChange={(key) => {
                    setActiveTab(key);
                    try {
                      router.setParams({ tab: key });
                    } catch {
                      /* router.setParams is platform-dependent — safe to swallow */
                    }
                  }}
                />
              </View>

              {/* Content */}
              <View className="p-5">
                {renderContent()}
              </View>
            </>
          )}
        </View>
      </ScrollView>

      {/* Modals */}
      {school && (
        <>
          {/* Unified invite modal — replaces StudentInviteModalEnhanced and
              MentorInviteModal. Schools can only invite student/mentor;
              admin/billing tabs are restricted to platform admin surfaces. */}
          <InvitePersonModal
            visible={invitePerson.visible}
            schoolId={school.id}
            initialRole={invitePerson.role}
            allowedRoles={['student', 'mentor']}
            onClose={closeInvite}
            onInvited={(role) => {
              if (role === 'mentor') {
                setMentorRefreshKey((k) => k + 1);
              } else {
                handleInviteSuccess();
              }
            }}
          />
          <StudentDetailModal
            visible={showStudentDetail}
            studentId={selectedStudentId}
            schoolId={school.id}
            onClose={handleCloseStudentDetail}
          />
          <DocumentFrameworkLinkSheet
            visible={pendingDocumentUpload !== null}
            fileName={pendingDocumentUpload?.file.name ?? ''}
            frameworks={pendingDocumentUpload?.frameworks ?? []}
            onCancel={cancelPendingDocumentUpload}
            onConfirm={confirmPendingDocumentUpload}
          />
          {selectedFrameworkId && (
            <FrameworkEditorModal
              visible={showFrameworkEditor}
              schoolId={school.id}
              frameworkId={selectedFrameworkId}
              onClose={closeFrameworkEditor}
              onSaved={() => {
                loadFrameworks();
              }}
            />
          )}
        </>
      )}
    </SafeAreaView>
  );
}
